<script setup lang="ts">
import { tpgIconClose } from '@tpg/grammar-web-icons';
import { computed, toRefs } from 'vue';

import {
  GrammarFooter,
  GrammarOverlayCard,
  GrammarPrimaryButton,
  GrammarQuaternaryButton,
} from '@nora-monorepo/grammar-ui';
import { t, useConfirmModalStore } from '@nora-monorepo/ui-shared';

import { TacticalAnnotationToolMode } from '#nora-ui/map/drawtools/stores/tactical-annotation-tool-store';
import type { TacticalAnnotationTool } from '#nora-ui/map/drawtools/tactical-annotation/tactical-annotation-tool';
import { useMapStore } from '#nora-ui/map/stores/map-store';

interface Props {
  mapId: string;
  tool: TacticalAnnotationTool;
}

const props = defineProps<Props>();
const { mapId, tool } = toRefs(props);
const mapStore = useMapStore();
const map = mapStore.getMapByIdOrThrow(mapId.value);
const drawToolUI = map.drawToolUI;
const confirmModalStore = useConfirmModalStore();

const tacticalAnnotationToolStore = computed(
  () => tool.value.tacticalAnnotationToolStore,
);

const mode = computed(() => tacticalAnnotationToolStore.value.mode);

async function onTargetConfirm() {
  tacticalAnnotationToolStore.value.mode = TacticalAnnotationToolMode.Done;
  await tool.value.save();
  tool.value.dismiss();
}

function onClose() {
  if (!drawToolUI.isEditing.value) {
    drawToolUI.setDrawToolByType(null);
    return;
  }

  if (
    drawToolUI.activeDrawToolType.value !== null
    && drawToolUI.activeDrawTool.value?.isEdited
  ) {
    confirmModalStore.showConfirmModal({
      headerText: 'Discard changes?',
      bodyText: 'Unsaved edits will be lost.',
      confirmButtonLabel: t('common.discard'),
      onConfirm: () => {
        drawToolUI.revertEdit();
      },
    });
  }
  else {
    drawToolUI.revertEdit();
  }
}
</script>

<template>
  <GrammarOverlayCard
    v-if="mode !== TacticalAnnotationToolMode.Done"
    label="Tactical Annotation"
    class="min-w-[300px] max-w-[400px]"
  >
    <template #header-trailing-buttons>
      <GrammarQuaternaryButton
        :icon-template="tpgIconClose"
        @click="onClose"
      />
    </template>
    <template v-if="mode !== TacticalAnnotationToolMode.Confirmation">
      <p class="text-lg p-2 text-primary-500 text-center">
        <template v-if="mode === TacticalAnnotationToolMode.CreateAreaPolygon">
          Click the corners around the area
        </template>
        <template v-if="mode === TacticalAnnotationToolMode.SelectEntrySide">
          Click the entry line
        </template>
      </p>
    </template>
    <template v-else>
      <p class="text-lg p-2 text-primary-500 text-center">
        Confirm to finish
      </p>
    </template>
    <template #footer>
      <GrammarFooter>
        <template #right-button>
          <GrammarPrimaryButton
            :disabled="mode !== TacticalAnnotationToolMode.Confirmation"
            size="regular"
            label="Confirm"
            @click="onTargetConfirm"
          />
        </template>
      </GrammarFooter>
    </template>
  </GrammarOverlayCard>
</template>
